/*=========================================================================================
	File Name: router.js
	Description: Routes for vue-router. Lazy loading is enabled.
	Object Strucutre:
										path => router path
										name => router name
										component(lazy loading) => component to load
										meta : {
											rule => which user can have access (ACL)
											breadcrumb => Add breadcrumb to specific page
											pageTitle => Display title besides breadcrumb
										}
	----------------------------------------------------------------------------------------
	Item Name: Vuesax Admin - VueJS Dashboard Admin Template
	Author: Pixinvent
	Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import auth from "@/auth/authService"
import store from './store/store'

Vue.use(Router)

// 防止访问相同路由报错
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
}

const router = new Router({
	mode: 'history',
	base: process.env.BASE_URL,
	scrollBehavior() {
		return { x: 0, y: 0 }
	},
	routes: [

		{
			// =============================================================================
			// MAIN LAYOUT ROUTES
			// =============================================================================
			path: '',
			component: () => import('./layouts/main/Main.vue'),
			children: [
				// =============================================================================
				// Theme Routes
				// =============================================================================
				{
					path: '/',
					redirect: '/dashboard'
				},
				{
					path: '/dashboard',
					name: 'dashboard',
					component: () => import('./views/dashboard.vue'),
					meta: {
						rule: 'student'
					}
				},
				// =============================================================================
				// teacherManage
				// =============================================================================
				{
				    path: '/teacher/list',
				    name: 'teacherList',
				    component: () => import('./views/teacher-manage/list.vue'),
				    meta: {
				        breadcrumb: [
				            { title: 'Home', url: '/' },
				            { title: '教师管理', url: null },
				            { title: '教师列表', active: true }
				        ],
				        pageTitle: '教师管理',
				        rule: 'admin'
				    },
				},
				// =============================================================================
				// classesManage
				// =============================================================================
				{
					path: '/classes/list',
					name: 'classesList',
					component: () => import('./views/classes-manage/list.vue'),
					meta: {
						breadcrumb: [
							{ title: 'Home', url: '/' },
							{ title: '班级管理', url: null },
							{ title: '班级列表', active: true }
						],
						pageTitle: '班级管理',
						rule: 'teacher'
					},
				},
				// =============================================================================
				// studentManage
				// =============================================================================
				{
					path: '/student/List',
					name: 'studentList',
					component: () => import('./views/student-manage/list.vue'),
					meta: {
						breadcrumb: [
							{ title: 'Home', url: '/' },
							{ title: '学生管理', url: null },
							{ title: '学生列表', active: true }
						],
						pageTitle: '学生管理',
						rule: 'teacher'
					},
				},
				// =============================================================================
				// courseCenter
				// =============================================================================
				{
					path: '/courseCenter',
					name: 'courseCenter',
					component: () => import('./views/course-center/list.vue'),
					meta: {
						breadcrumb: [
							{ title: 'Home', url: '/' },
							{ title: '课程中心', active: true }
						],
						pageTitle: '课程中心',
						rule: 'student'
					},
				},
				{
					path: '/courseCenter/contents',
					name: 'courseCenterContents',
					component: () => import('./views/course-center/contents.vue'),
					meta: {
						breadcrumb: [
							{ title: 'Home', url: '/' },
							{ title: '课程中心', url: '/courseCenter' },
							{ title: '课程目录', active: true }
						],
						pageTitle: '课程目录',
						rule: 'student'
					},
				},
				//=============================================================================
				// creativeTools
				// =============================================================================
				{
					path: '/creativeTools',
					name: 'creativeTools',
					component: () => import('./views/creative-tools/list.vue'),
					meta: {
						breadcrumb: [
							{ title: 'Home', url: '/' },
							{ title: '创作工具', active: true }
						],
						pageTitle: '创作工具',
						rule: 'student'
					},
				},
				//=============================================================================
				// worksManage
				// =============================================================================
				{
					path: '/works/list',
					name: 'worksList',
					component: () => import('./views/works-manage/list.vue'),
					meta: {
						breadcrumb: [
							{ title: 'Home', url: '/' },
							{ title: '作品管理', url: null },
							{ title: '作品列表', active: true }
						],
						pageTitle: '作品管理',
						rule: 'student'
					},
				},
				//=============================================================================
				// termReport
				// =============================================================================
				{
					path: '/termReport',
					name: 'termReport',
					component: () => import('./views/report/termReport.vue'),
					meta: {
						breadcrumb: [
							{ title: 'Home', url: '/' },
							{ title: '学期汇报', url: null },
							{ title: '2021第一学期', active: true }
						],
						pageTitle: '学期汇报',
						rule: 'student'
					},
				},
				{
					path: '/report/classReport',
					name: 'classReport',
					component: () => import('./views/report/classReport.vue'),
					meta: {
						breadcrumb: [
							{ title: 'Home', url: '/' },
							{ title: '学期汇报', url: '/termReport' },
							{ title: '班级汇报', active: true }
						],
						pageTitle: '学期汇报',
						rule: 'teacher'
					},
				},
				// =============================================================================
        // USERS
        // =============================================================================
				{
					path: '/user/profile',
					name: 'userProfile',
					component: () => import('./views/user/Profile.vue'),
					meta: {
							breadcrumb: [
									{ title: 'Home', url: '/' },
									{ title: '个人信息', active: true }
							],
							pageTitle: '个人信息',
							rule: 'student'
					},
				},
				// =============================================================================
				// Message
				// =============================================================================
				{
					path: '/message/list',
					name: 'message/List',
					component: () => import('./views/message/List.vue'),
					meta: {
						breadcrumb: [
							{ title: 'Home', 'url': '/' },
							{ title: '消息列表', active: true },
						],
						pageTitle: '消息列表',
						rule: 'student',
						authRequired: true
					}
				}
			],
		},
		// =============================================================================
		// FULL PAGE LAYOUTS
		// =============================================================================
		{
			path: '',
			component: () => import('./layouts/full-page/FullPage.vue'),
			children: [
				// =============================================================================
				// PAGES
				// =============================================================================
				{
					path: '/pages/login',
					name: 'pageLogin',
					component: () => import('@/views/pages/Login.vue'),
					meta: {
						rule: 'public',
					}
				},
				{
					path: '/pages/register',
					name: 'pageRegister',
					component: () => import('@/views/pages/Register.vue'),
					meta: {
						rule: 'public'
					}
				},
				{
					path: '/pages/forgot-password',
					name: 'pageForgotPassword',
					component: () => import('@/views/pages/ForgotPassword.vue'),
					meta: {
						rule: 'public'
					}
				},
				{
					path: '/pages/reset-password',
					name: 'pageResetPassword',
					component: () => import('@/views/pages/ResetPassword.vue'),
					meta: {
						rule: 'public'
					}
				},
				{
					path: '/pages/permissionSelect',
					name: 'permissionSelect',
					component: () => import('@/views/pages/permissionSelect.vue'),
					meta: {
						rule: 'public',
					}
				},
				{
					path: '/pages/lock-screen',
					name: 'pageLockScreen',
					component: () => import('@/views/pages/LockScreen.vue'),
					meta: {
						rule: 'student'
					}
				},
				{
					path: '/pages/comingsoon',
					name: 'pageComingSoon',
					component: () => import('@/views/pages/ComingSoon.vue'),
					meta: {
						rule: 'public'
					}
				},
				{
					path: '/pages/error-404',
					name: 'pageError404',
					component: () => import('@/views/pages/Error404.vue'),
					meta: {
						rule: 'public'
					}
				},
				{
					path: '/pages/error-500',
					name: 'pageError500',
					component: () => import('@/views/pages/Error500.vue'),
					meta: {
						rule: 'public'
					}
				},
				{
					path: '/pages/not-authorized',
					name: 'pageNotAuthorized',
					component: () => import('@/views/pages/NotAuthorized.vue'),
					meta: {
						rule: 'public'
					}
				},
				{
					path: '/pages/maintenance',
					name: 'pageMaintenance',
					component: () => import('@/views/pages/Maintenance.vue'),
					meta: {
						rule: 'student'
					}
				},
				{
					path: '/courseCenter/detail',
					name: 'courseCenterDetail',
					component: () => import('@/views/course-center/detail.vue'),
					meta: {
						rule: 'student'
					}
				},
				{
					path: '/task/grade',
					name: 'taskGrad',
					component: () => import('@/views/task/grade.vue'),
					meta: {
						rule: 'teacher'
					}
				},
				{
					path: '/works/markWork',
					name: 'worksMarkWork',
					component: () => import('@/views/works-manage/mark-works.vue'),
					meta: {
						rule: 'teacher'
					}
				},
			]
		},
		// Redirect to 404 page, if no match found
		{
			path: '*',
			redirect: '/pages/error-404'
		}
	],
})

router.afterEach(() => {
	// Remove initial loading
	const appLoading = document.getElementById('loading-bg')
	if (appLoading) {
		appLoading.style.display = "none";
	}
})

router.beforeEach((to, from, next) => {
	console.log(to.fullPath)
	if (
		to.path === "/pages/login" ||
		to.path === "/pages/forgot-password" ||
		to.path === "/pages/error-404" ||
		to.path === "/pages/error-500" ||
		to.path === "/pages/register" ||
		to.path === "/pages/comingsoon" ||
		(auth.isAuthenticated())
	) {
		return next()
	}
	// 登录后带参数跳转
	router.push({ path: '/pages/login', query: { to: to.fullPath } })
	// Specify the current path as the customState parameter, meaning it
	// will be returned to the application after auth
	// auth.login({ target: to.path });

});

export default router
